import styled from 'styled-components'
import { useEffect, useRef, useCallback } from 'react'
import { SendOutline } from 'share-ui/components/Icon/Icons'
import debounce from 'lodash/debounce'
import { ButtonTertiary } from 'components/Button/Button'

const ChatInput = ({
    formik,
    fieldName,
    stopRequest,
}: {
    formik: any
    fieldName: string
    stopRequest?: {
        requestLoading: boolean
        handleStopRequest: () => void
    }
}) => {
    const inputRef = useRef<HTMLTextAreaElement>(null)

    useEffect(() => {
        if (inputRef.current) {
            const length = formik.values[fieldName].length
            inputRef.current.setSelectionRange(length, length)
            adjustTextareaHeight(inputRef.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values[fieldName]])

    const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
        textarea.style.height = 'auto'
        textarea.style.height = `${textarea.scrollHeight}px`
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSetFieldValue = useCallback(
        debounce(value => {
            formik.setFieldValue(fieldName, value)
        }, 1),
        [],
    )

    const handleSubmit = () => {
        if (!stopRequest?.requestLoading) {
            formik.handleSubmit()
        }
    }

    return (
        <Container>
            {stopRequest?.requestLoading && (
                <StyledStopButton>
                    <ButtonTertiary onClick={stopRequest?.handleStopRequest}>
                        Cancel generation
                    </ButtonTertiary>
                </StyledStopButton>
            )}

            <CustomTextField
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                ref={inputRef}
                value={formik.values[fieldName]}
                name={fieldName}
                onChange={e => {
                    debouncedSetFieldValue(e.target.value)
                    if (inputRef.current) {
                        adjustTextareaHeight(inputRef.current)
                    }
                }}
                placeholder='Type your message...'
                rows={1}
                onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSubmit()
                    }
                }}
                onInput={() => {
                    if (inputRef.current) {
                        adjustTextareaHeight(inputRef.current)
                    }
                }}
            />

            <StyledButtonWrapper>
                <CustomIconButton
                    onClick={handleSubmit}
                    disabled={!formik.values[fieldName].trim() || stopRequest?.requestLoading}
                >
                    <SendOutline size={34} />
                </CustomIconButton>
            </StyledButtonWrapper>
        </Container>
    )
}

export default ChatInput

const Container = styled.div`
    display: flex;
    align-items: flex-end;
    width: 100%;
    padding: 0 4px;
    padding-bottom: 10px;
    position: relative;
`

const CustomTextField = styled.textarea`
    width: 100%;
    border-radius: 25px;
    padding: 16px;
    padding-right: 50px;
    padding-left: 24px;
    box-shadow: ${({ theme }) => theme.body.boxShadow};
    background-color: ${({ theme }) => theme.textFiled.primary.activeBgColor};
    color: ${({ theme }) => theme.textFiled.primary.color};
    border: 1px solid transparent;
    resize: none;
    overflow: hidden;
    outline: none;

    max-height: 200px;

    &:hover {
        border-color: transparent;
    }

    &:focus {
        border-color: transparent;
    }
`

const CustomIconButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.body.textColorPrimary};
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        color: ${({ theme }) => theme.body.textDisabled};
        cursor: not-allowed;
    }

    transform: rotate(44deg);
`

const StyledButtonWrapper = styled.div`
    position: absolute;
    right: 16px;
    bottom: 22px;
`

const StyledStopButton = styled.div`
    position: absolute;
    bottom: 75px;
    left: 50%;
    transform: translateX(-50%);

    border-radius: 20px;

    background-color: ${({ theme }) => theme.body.backgroundColorSecondary};
    backdrop-filter: blur(100px);
`
